import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { socialApi } from '@/api';
import { Button } from '@/components/ui/Button';
import { DataTable, type Column } from '@/components/ui/DataTable';
import { usePagination, dataTablePageProps } from '@/hooks/usePagination';
import type { SocialPostResponse } from '@/types/social';
import { showApiError } from '@/utils/apiError';
import { notify } from '@/core/toast/notify';
import { useSocialAccounts } from '../hooks/useSocialAccounts';

interface SocialPostsWorkspaceProps {
  platformFilter?: string;
  showComposeLink?: boolean;
}

export function SocialPostsWorkspace({ platformFilter, showComposeLink = true }: SocialPostsWorkspaceProps) {
  const pagination = usePagination();
  const { connected, byPlatform } = useSocialAccounts();
  const [data, setData] = useState<SocialPostResponse[]>([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    const accountIds = platformFilter
      ? byPlatform(platformFilter).map((a) => a.id)
      : connected.map((a) => a.id);
    setLoading(true);
    try {
      const posts = await socialApi.listPosts({ page: pagination.page, size: pagination.size });
      let rows = posts.content;
      if (platformFilter && accountIds.length > 0) {
        rows = rows.filter((p) =>
          p.channelAccountIds?.some((id) => accountIds.includes(id)),
        );
      }
      setData(rows);
      pagination.applyPageResponse(posts);
      if (platformFilter) pagination.applyClientCount(rows.length);
    } catch (err) {
      showApiError(err, 'Failed to load posts');
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.size, platformFilter, connected, byPlatform]);

  useEffect(() => {
    void load();
  }, [load]);

  const publish = async (id: number) => {
    try {
      await socialApi.publishPost(id);
      notify.success('Published');
      void load();
    } catch (err) {
      showApiError(err, 'Publish failed');
    }
  };

  const columns: Column<SocialPostResponse>[] = [
    { key: 'id', header: '#' },
    { key: 'postStatus', header: 'Status' },
    { key: 'content', header: 'Content', render: (r) => r.content.slice(0, 100) },
    {
      key: 'actions',
      header: '',
      render: (r) =>
        r.postStatus === 'DRAFT' || r.postStatus === 'SCHEDULED' ? (
          <Button size="sm" onClick={() => void publish(r.id)}>
            Publish
          </Button>
        ) : null,
    },
  ];

  return (
    <div>
      {showComposeLink && (
        <div className="mb-4 flex justify-end">
          <Link to="/social/publishing">
            <Button>Open Publishing Center</Button>
          </Link>
        </div>
      )}
      <DataTable
        columns={columns}
        data={data}
        keyField="id"
        loading={loading}
        emptyMessage="No posts for this platform yet."
        {...dataTablePageProps(pagination)}
      />
      <p className="mt-2 text-xs text-slate-500">
        Likes, comments, and shares per post sync via analytics — view in Analytics Center.
      </p>
    </div>
  );
}
